import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { CSSProperties } from 'react';

export interface GuideStep {
  /** Selector CSS del elemento junto al que se posiciona el popover. */
  targetSelector: string;
  title: string;
  text: string;
}

interface GuideOverlayProps {
  steps: GuideStep[];
  isOpen: boolean;
  /** Se llama al terminar el tour o al saltarlo — el consumidor decide qué persistir. */
  onClose: () => void;
  nextLabel: string;
  skipLabel: string;
  doneLabel: string;
}

const VIEWPORT_MARGIN = 12;
const CENTERED_STYLE: CSSProperties = { top: '40%', left: '50%', transform: 'translate(-50%, -50%)' };

function targetRect(selector: string): DOMRect | null {
  const element = document.querySelector(selector);
  return element ? element.getBoundingClientRect() : null;
}

/** Posición del popover respecto al elemento resaltado, recortada al viewport: cae debajo del
 *  elemento cuando hay espacio, o arriba cuando no lo hay (evita que quede cortado por el borde
 *  inferior de la pantalla en pasos cercanos al final del post-it). El ancho/alto reales del
 *  popover (`popoverSize`) solo se conocen tras su primer render — ver `useLayoutEffect` abajo. */
function computePopoverStyle(rect: DOMRect | null, popoverSize: { width: number; height: number }): CSSProperties {
  if (!rect) return CENTERED_STYLE;
  const fitsBelow = rect.bottom + VIEWPORT_MARGIN + popoverSize.height <= window.innerHeight - VIEWPORT_MARGIN;
  const top = fitsBelow
    ? rect.bottom + VIEWPORT_MARGIN
    : Math.max(VIEWPORT_MARGIN, rect.top - VIEWPORT_MARGIN - popoverSize.height);
  const maxLeft = Math.max(VIEWPORT_MARGIN, window.innerWidth - popoverSize.width - VIEWPORT_MARGIN);
  const left = Math.min(Math.max(VIEWPORT_MARGIN, rect.left), maxLeft);
  return { top, left };
}

/** Tour de coachmarks reutilizable: resalta un elemento por paso con un popover flotante. */
export function GuideOverlay({ steps, isOpen, onClose, nextLabel, skipLabel, doneLabel }: Readonly<GuideOverlayProps>) {
  const [stepIndex, setStepIndex] = useState(0);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [popoverStyle, setPopoverStyle] = useState<CSSProperties>(CENTERED_STYLE);

  useEffect(() => {
    if (isOpen) setStepIndex(0);
  }, [isOpen]);

  const currentStep = isOpen && steps.length > 0 ? steps[Math.min(stepIndex, steps.length - 1)] : null;

  // El popover es `position: absolute` dentro de `.guide-overlay`, que es `position: fixed` — su
  // bloque de contención es el viewport, así que `getBoundingClientRect()` ya viene relativo a él.
  // Sumar `window.scrollY` desplazaría el popover fuera de pantalla en cualquier reapertura de la
  // guía tras hacer scroll (el caso típico del botón "?"). Se mide DESPUÉS del render (no se puede
  // conocer el tamaño real del popover — varía según el texto de cada paso — antes de pintarlo) y
  // `useLayoutEffect` corre antes de que el navegador pinte, así que no hay parpadeo visible.
  useLayoutEffect(() => {
    if (!currentStep) return;
    const rect = targetRect(currentStep.targetSelector);
    const popover = popoverRef.current;
    const size = popover
      ? { width: popover.offsetWidth, height: popover.offsetHeight }
      : { width: 0, height: 0 };
    setPopoverStyle(computePopoverStyle(rect, size));
  }, [currentStep]);

  if (!currentStep) return null;
  const step = currentStep;
  const isLast = stepIndex === steps.length - 1;

  // Portal a `document.body`: `.guide-overlay` es `position: fixed` para cubrir siempre el
  // viewport completo, pero cualquier ancestro con `backdrop-filter`/`filter`/`transform` (p.ej.
  // `.shine-card` en la pantalla de Brillamos) crea un containing block propio para descendientes
  // `fixed` — el overlay quedaba encogido al tamaño de esa tarjeta en vez de la pantalla, con el
  // popover calculado fuera de esa caja (invisible) y el scrim, del tamaño de la tarjeta, seguía
  // interceptando cada click sobre el post-it (nombre, mensaje, banco de palabras) hasta cerrarse.
  // Montar en `document.body` saca el overlay de cualquier ancestro con esas propiedades.
  return createPortal(
    <div className="guide-overlay" role="dialog" aria-modal="true" aria-label={step.title}>
      <button type="button" className="guide-overlay-scrim" aria-label={skipLabel} onClick={onClose} />
      <div ref={popoverRef} className="guide-overlay-popover" style={popoverStyle}>
        <h4 className="guide-overlay-title">{step.title}</h4>
        <p className="guide-overlay-text">{step.text}</p>
        <div className="guide-overlay-dots">
          {steps.map((current, index) => (
            <span
              key={current.targetSelector}
              className={`guide-overlay-dot ${index === stepIndex ? 'guide-overlay-dot--active' : ''}`}
            />
          ))}
        </div>
        <div className="guide-overlay-actions">
          <button type="button" className="guide-overlay-skip" onClick={onClose}>{skipLabel}</button>
          <button
            type="button"
            className="guide-overlay-next"
            onClick={() => (isLast ? onClose() : setStepIndex((prev) => prev + 1))}
          >
            {isLast ? doneLabel : nextLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default GuideOverlay;
